import React, { useState } from 'react';
import { Card, Row, Col, Tag, Statistic, Progress, Tabs, Timeline, Table } from 'antd';
import { 
  EnvironmentOutlined, 
  ClockCircleOutlined, 
  CheckCircleOutlined,
  BarChartOutlined,
  SafetyCertificateOutlined
} from '@ant-design/icons';
import { motion } from 'framer-motion';

const { TabPane } = Tabs;

interface AirportInfo {
  code: string;
  name: string;
  city: string;
  advantages: string[];
  clearanceTime: string;
  successRate: number;
  dailyCapacity: string;
  specialFeatures: string[];
  coverageArea: string[];
  operatingHours: string;
  customsProcess: {
    step: string;
    time: string;
    details: string;
  }[];
}

const airportsData: AirportInfo[] = [
  {
    code: 'LAX',
    name: '洛杉矶国际机场',
    city: '洛杉矶',
    advantages: [
      '美西地区最大清关口岸',
      '处理能力强，效率高',
      '覆盖美西全境配送',
      '华人聚集区，清关便利'
    ],
    clearanceTime: '12-24小时',
    successRate: 98.5,
    dailyCapacity: '5000+票',
    specialFeatures: [
      '24小时海关作业',
      '专业中文服务团队',
      '完善的仓储分拣系统',
      'FBA专线直达服务'
    ],
    coverageArea: ['加州全境', '内华达州', '亚利桑那州', '犹他州'],
    operatingHours: '全天24小时',
    customsProcess: [
      { step: '货物到港', time: '0-2小时', details: '航班降落，货物卸载至海关监管区' },
      { step: 'ISF核验', time: '2-6小时', details: '海关核验ISF申报信息，匹配舱单数据' },
      { step: '文件审核', time: '6-12小时', details: '审核商业发票、装箱单等清关文件' },
      { step: '缴税放行', time: '12-24小时', details: '完成关税缴纳，获得海关放行指令' }
    ]
  },
  {
    code: 'SFO',
    name: '旧金山国际机场',
    city: '旧金山',
    advantages: [
      '湾区科技产品清关优势',
      '电子产品审核经验丰富',
      '硅谷企业首选口岸',
      '清关速度业界领先'
    ],
    clearanceTime: '8-18小时',
    successRate: 97.8,
    dailyCapacity: '3500+票',
    specialFeatures: [
      '科技产品绿色通道',
      '先进的自动化系统',
      'FDA认证产品快速通道',
      '专业电子产品检验团队'
    ],
    coverageArea: ['北加州', '俄勒冈州', '华盛顿州'],
    operatingHours: '周一至周日 6:00-22:00',
    customsProcess: [
      { step: '货物到港', time: '0-1小时', details: '高效的货物处理流程，快速进入监管区' },
      { step: '智能分拣', time: '1-4小时', details: '自动化系统进行货物分类和初步审核' },
      { step: '专业审核', time: '4-12小时', details: '针对科技产品的专业化审核流程' },
      { step: '快速放行', time: '8-18小时', details: '绿色通道快速完成清关手续' }
    ]
  },
  {
    code: 'JFK',
    name: '约翰·肯尼迪国际机场',
    city: '纽约',
    advantages: [
      '美东地区核心枢纽',
      '国际贸易经验最丰富',
      '覆盖美东北部全境',
      '金融中心贸易便利'
    ],
    clearanceTime: '18-36小时',
    successRate: 96.2,
    dailyCapacity: '6000+票',
    specialFeatures: [
      '国际贸易中心优势',
      '多语种专业服务',
      '复杂商品处理经验',
      '华尔街金融产品通道'
    ],
    coverageArea: ['纽约州', '新泽西州', '康涅狄格州', '宾夕法尼亚州'],
    operatingHours: '全天24小时',
    customsProcess: [
      { step: '货物到港', time: '0-3小时', details: '大型国际机场，货量大处理相对较慢' },
      { step: '排队等候', time: '3-12小时', details: '货量大，需要排队等候海关审核' },
      { step: '详细审核', time: '12-24小时', details: '严格的文件审核和货物检验程序' },
      { step: '完成清关', time: '18-36小时', details: '完成所有手续，货物放行提取' }
    ]
  },
  {
    code: 'ORD',
    name: '芝加哥奥黑尔国际机场',
    city: '芝加哥',
    advantages: [
      '美国中部交通枢纽',
      '辐射中西部全境',
      '工业品清关优势',
      '内陆配送成本低'
    ],
    clearanceTime: '24-48小时',
    successRate: 95.8,
    dailyCapacity: '4500+票',
    specialFeatures: [
      '中西部配送中心',
      '工业产品专业处理',
      '农产品检疫快速通道',
      '内陆运输成本优势'
    ],
    coverageArea: ['伊利诺伊州', '密歇根州', '威斯康星州', '印第安纳州'],
    operatingHours: '周一至周六 8:00-20:00',
    customsProcess: [
      { step: '货物到港', time: '0-4小时', details: '中西部枢纽，货物处理相对从容' },
      { step: '分类处理', time: '4-12小时', details: '按货物类型进行专业化分类处理' },
      { step: '工业品审核', time: '12-36小时', details: '工业品和农产品的专业审核流程' },
      { step: '内陆放行', time: '24-48小时', details: '完成清关，优势在于内陆配送' }
    ]
  }
];

const USCustomsAirports: React.FC = () => {
  const [activeAirport, setActiveAirport] = useState('LAX');

  const tableColumns = [
    {
      title: '机场代码',
      dataIndex: 'code',
      key: 'code',
      width: 100,
      render: (code: string) => <Tag color="blue" style={{ fontSize: '14px' }}>{code}</Tag>
    },
    {
      title: '机场名称',
      dataIndex: 'name',
      key: 'name',
      width: 200
    },
    {
      title: '清关时效',
      dataIndex: 'clearanceTime',
      key: 'clearanceTime',
      width: 120,
      render: (time: string) => <Tag color="orange">{time}</Tag>
    },
    {
      title: '成功率',
      dataIndex: 'successRate',
      key: 'successRate',
      width: 100,
      render: (rate: number) => (
        <div>
          <Progress 
            type="circle" 
            size={50} 
            percent={rate} 
            format={percent => `${percent}%`}
            strokeColor={rate > 97 ? '#52c41a' : rate > 95 ? '#faad14' : '#ff4d4f'}
          />
        </div>
      )
    },
    {
      title: '日处理能力',
      dataIndex: 'dailyCapacity',
      key: 'dailyCapacity',
      width: 120,
      render: (capacity: string) => <Tag color="green">{capacity}</Tag>
    },
    {
      title: '覆盖区域',
      dataIndex: 'coverageArea',
      key: 'coverageArea',
      render: (areas: string[]) => (
        <div>
          {areas.slice(0, 2).map(area => (
            <Tag key={area} color="geekblue" style={{ margin: '2px' }}>{area}</Tag>
          ))}
          {areas.length > 2 && <span style={{ color: '#666' }}>等{areas.length}个州</span>}
        </div>
      )
    }
  ];

  return (
    <div style={{ padding: '40px 0' }}>
      {/* 标题区域 */}
      <div style={{ textAlign: 'center', marginBottom: '48px' }}>
        <motion.h2
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          style={{ 
            fontSize: '2.5rem',
            fontWeight: '700',
            color: '#1D1D1F',
            marginBottom: '16px'
          }}
        >
          四大清关枢纽机场
        </motion.h2>
        <motion.p
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ delay: 0.1 }}
          style={{ 
            fontSize: '1.125rem',
            color: '#666',
            maxWidth: '600px',
            margin: '0 auto'
          }}
        >
          深度解析美国四大主要清关机场，专业数据助您精准选择最优清关路径
        </motion.p>
      </div>

      {/* 机场对比表格 */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        style={{ marginBottom: '48px' }}
      >
        <Card 
          title={
            <div style={{ display: 'flex', alignItems: 'center', gap: '8px' }}>
              <BarChartOutlined style={{ color: '#1890ff' }} />
              <span>四大机场综合对比</span>
            </div>
          }
          style={{ borderRadius: '12px', boxShadow: '0 4px 20px rgba(0,0,0,0.08)' }}
        >
          <Table 
            dataSource={airportsData}
            columns={tableColumns}
            pagination={false}
            rowKey="code"
            size="large"
            onRow={(record) => ({
              onClick: () => setActiveAirport(record.code),
              style: { cursor: 'pointer' }
            })}
            rowClassName={(record) => record.code === activeAirport ? 'ant-table-row-selected' : ''}
          />
        </Card>
      </motion.div>

      {/* 详细信息标签页 */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.2 }}
      >
        <Tabs 
          activeKey={activeAirport}
          onChange={setActiveAirport}
          size="large"
          type="card"
          tabBarStyle={{ marginBottom: '24px' }}
        >
          {airportsData.map((airport) => (
            <TabPane
              tab={
                <div style={{ padding: '8px 16px', textAlign: 'center' }}>
                  <div style={{ fontSize: '18px', fontWeight: '600', color: '#1890ff' }}>
                    {airport.code}
                  </div>
                  <div style={{ fontSize: '12px', color: '#666' }}>
                    {airport.city}
                  </div>
                </div>
              }
              key={airport.code}
            >
              <Row gutter={[24, 24]}>
                {/* 基本信息 */}
                <Col xs={24} lg={8}>
                  <Card 
                    title="基本信息"
                    style={{ height: '100%', borderRadius: '8px' }}
                    headStyle={{ background: '#f8f9ff', border: 'none' }}
                  >
                    <div style={{ marginBottom: '24px' }}>
                      <h3 style={{ color: '#1890ff', marginBottom: '8px', fontSize: '20px' }}>
                        {airport.name}
                      </h3>
                      <div style={{ display: 'flex', alignItems: 'center', gap: '8px', marginBottom: '16px' }}>
                        <EnvironmentOutlined style={{ color: '#666' }} />
                        <span style={{ color: '#666' }}>{airport.city}</span>
                      </div>
                    </div>

                    <Row gutter={[16, 16]}>
                      <Col span={12}>
                        <Statistic
                          title="清关成功率"
                          value={airport.successRate}
                          suffix="%"
                          valueStyle={{ color: '#52c41a', fontSize: '24px' }}
                        />
                      </Col>
                      <Col span={12}>
                        <Statistic
                          title="日处理量"
                          value={airport.dailyCapacity}
                          valueStyle={{ color: '#1890ff', fontSize: '20px' }}
                        />
                      </Col>
                    </Row>

                    <div style={{ marginTop: '24px' }}>
                      <div style={{ marginBottom: '12px', fontWeight: '600', color: '#333' }}>
                        <ClockCircleOutlined style={{ marginRight: '8px' }} />
                        作业时间
                      </div>
                      <Tag color="blue" style={{ fontSize: '14px' }}>
                        {airport.operatingHours}
                      </Tag>
                    </div>

                    <div style={{ marginTop: '20px' }}>
                      <div style={{ marginBottom: '12px', fontWeight: '600', color: '#333' }}>
                        清关时效
                      </div>
                      <Tag color="orange" style={{ fontSize: '16px', padding: '4px 12px' }}>
                        {airport.clearanceTime}
                      </Tag>
                    </div>
                  </Card>
                </Col>

                {/* 清关流程 */}
                <Col xs={24} lg={16}>
                  <Card 
                    title="清关流程详解"
                    style={{ height: '100%', borderRadius: '8px' }}
                    headStyle={{ background: '#f8f9ff', border: 'none' }}
                  >
                    <Timeline mode="left">
                      {airport.customsProcess.map((process, index) => (
                        <Timeline.Item
                          key={index}
                          dot={
                            <div style={{
                              width: '32px',
                              height: '32px',
                              borderRadius: '50%',
                              background: '#1890ff',
                              display: 'flex',
                              alignItems: 'center',
                              justifyContent: 'center',
                              color: 'white',
                              fontSize: '14px',
                              fontWeight: '600'
                            }}>
                              {index + 1}
                            </div>
                          }
                        >
                          <div style={{ paddingLeft: '16px' }}>
                            <div style={{ display: 'flex', alignItems: 'center', gap: '12px', marginBottom: '8px' }}>
                              <h4 style={{ margin: 0, color: '#1890ff', fontSize: '16px' }}>
                                {process.step}
                              </h4>
                              <Tag color="green">{process.time}</Tag>
                            </div>
                            <p style={{ margin: 0, color: '#666', lineHeight: '1.6' }}>
                              {process.details}
                            </p>
                          </div>
                        </Timeline.Item>
                      ))}
                    </Timeline>
                  </Card>
                </Col>

                {/* 核心优势 */}
                <Col xs={24} lg={12}>
                  <Card 
                    title="核心优势"
                    style={{ borderRadius: '8px' }}
                    headStyle={{ background: '#f8f9ff', border: 'none' }}
                  >
                    <div style={{ display: 'grid', gap: '12px' }}>
                      {airport.advantages.map((advantage, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, x: -20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.1 }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '12px',
                            background: '#f8f9ff',
                            borderRadius: '6px',
                            border: '1px solid #e6f7ff'
                          }}
                        >
                          <CheckCircleOutlined style={{ color: '#52c41a', fontSize: '18px' }} />
                          <span style={{ color: '#333', fontSize: '14px' }}>{advantage}</span>
                        </motion.div>
                      ))}
                    </div>
                  </Card>
                </Col>

                {/* 特色服务 */}
                <Col xs={24} lg={12}>
                  <Card 
                    title="特色服务"
                    style={{ borderRadius: '8px' }}
                    headStyle={{ background: '#f8f9ff', border: 'none' }}
                  >
                    <div style={{ display: 'grid', gap: '12px' }}>
                      {airport.specialFeatures.map((feature, index) => (
                        <motion.div
                          key={index}
                          initial={{ opacity: 0, x: 20 }}
                          whileInView={{ opacity: 1, x: 0 }}
                          viewport={{ once: true }}
                          transition={{ delay: index * 0.1 }}
                          style={{
                            display: 'flex',
                            alignItems: 'center',
                            gap: '12px',
                            padding: '12px',
                            background: '#fff2e8',
                            borderRadius: '6px',
                            border: '1px solid #ffd8bf'
                          }}
                        >
                          <SafetyCertificateOutlined style={{ color: '#fa8c16', fontSize: '18px' }} />
                          <span style={{ color: '#333', fontSize: '14px' }}>{feature}</span>
                        </motion.div>
                      ))}
                    </div>
                  </Card>
                </Col>
              </Row>
            </TabPane>
          ))}
        </Tabs>
      </motion.div>

      {/* 专业建议 */}
      <motion.div
        initial={{ opacity: 0, y: 30 }}
        whileInView={{ opacity: 1, y: 0 }}
        viewport={{ once: true }}
        transition={{ delay: 0.3 }}
        style={{ marginTop: '48px' }}
      >
        <Card
          style={{
            background: '#ffffff',
            borderRadius: '12px',
            border: '1px solid #d2d2d7',
            color: '#1d1d1f',
            boxShadow: '0 4px 12px rgba(0, 0, 0, 0.06)',
          }}
          bodyStyle={{ padding: '32px' }}
        >
          <Row align="middle">
            <Col xs={24} md={16}>
              <h3 style={{ color: '#1d1d1f', fontSize: '24px', marginBottom: '16px', fontWeight: 600 }}>
                专业建议
              </h3>
              <p style={{ color: '#6e6e73', fontSize: '16px', marginBottom: '12px' }}>
                • <strong>时效优先</strong>：选择 SFO（8-18小时）或 LAX（12-24小时）
              </p>
              <p style={{ color: '#6e6e73', fontSize: '16px', marginBottom: '12px' }}>
                • <strong>成本考虑</strong>：选择 ORD 内陆配送成本更低
              </p>
              <p style={{ color: '#6e6e73', fontSize: '16px', marginBottom: '0' }}>
                • <strong>科技产品</strong>：SFO 有专门的绿色通道和专业团队
              </p>
            </Col>
            <Col xs={24} md={8} style={{ textAlign: 'center' }}>
              <div style={{ fontSize: '48px', marginBottom: '16px', lineHeight: 1 }} aria-hidden>
                🛫
              </div>
              <div style={{ color: '#6e6e73', fontSize: '14px' }}>
                选择适合的清关机场
                <br />
                让您的货物更快到达
              </div>
            </Col>
          </Row>
        </Card>
      </motion.div>
    </div>
  );
};

export default USCustomsAirports;
